
import { defineComponent, ref, onMounted } from "vue";
import { MBDatatable } from "magicbean-saas-common";
import AddMarketingCampaignsModal from "@/views/interactive-marketing/marketing-campaigns/AddMarketingCampaignsModal.vue";
import FilterDropdwon from "@/views/interactive-marketing/marketing-campaigns/FilterDropdown.vue";
import { MenuComponent } from "@/assets/ts/components";
import { ApiCampaign } from "@/core/api";
import moment from "moment";
import _ from "lodash";
import { useI18n } from "vue-i18n";
import { formatDate, setModuleBCN } from "@/core/directive/function/common";
import { useRoute } from "vue-router";

interface InfluencerGroups {
  id: number;
  name: string;
  description: string;
}

interface SortOption {
  field: string;
  direction: string;
}

interface FilterOption {
  field: string;
  value: string;
  from?: string;
  to?: number;
  condition?: string;
}

export default defineComponent({
  name: "marketing-campaigns",
  components: {
    MBDatatable,
    FilterDropdwon,
    AddMarketingCampaignsModal,
  },
  setup() {
    const route = useRoute();

    const loading = ref(true);
    const tableData = ref<Array<InfluencerGroups>>([]);
    const currentPage = ref<number>(1);
    const total = ref<number>(1);
    const pageSize = ref<number>(10);
    const search = ref<string>("");
    const sortOptions = ref<Array<SortOption>>([
      { field: "id", direction: "desc" },
    ]);
    const filterOptions = ref<Array<FilterOption>>([]);
    const { t } = useI18n();
    const tableHeader = ref([
      // {
      //   name: t("marketingCampaigns.id"),
      //   key: "id",
      //   sortable: true,
      // },
      {
        name: t("marketingCampaigns.campaignName"),
        key: "name",
        sortable: false,
      },
      {
        name: t("marketingCampaigns.campaignType"),
        key: "type",
        sortable: false,
      },
      {
        name: t("marketingCampaigns.campaignPurpose"),
        key: "purpose",
        sortable: false,
      },
      {
        name: t("marketingCampaigns.startDate"),
        key: "start_date",
        sortable: true,
      },
      {
        name: t("marketingCampaigns.endDate"),
        key: "end_date",
        sortable: true,
      },
      {
        name: t("marketingCampaigns.description"),
        key: "description",
        sortable: false,
      },
      {
        name: t("marketingCampaigns.status"),
        key: "status",
        sortable: false,
      },
      {
        name: "", // t("common.actions")
        key: "actions",
        sortable: false,
      },
    ]);

    onMounted(() => {
      MenuComponent.reinitialization();
      setModuleBCN(t, route);
      getCampaignList(
        currentPage.value,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    });

    const getCampaignList = (
      page: number,
      pageSize: number,
      search?: string,
      sortOptions?: Array<SortOption>,
      filterOptions?: Array<FilterOption>
    ) => {
      loading.value = true;
      ApiCampaign.getCampaignList({
        page: page,
        page_size: pageSize,
        search_value: search,
        sort_orders: sortOptions,
        filter_group: filterOptions,
      })
        .then(({ data }) => {
          loading.value = false;
          if (data.code == 0) {
            tableData.value.splice(
              0,
              tableData.value.length,
              ...data.data.items
            );
            total.value = data.data.total;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    };

    const getStatus = (status) => {
      let str = "",
        statusClass = "";
      switch (status) {
        case 10:
          str = t("marketingCampaigns.plan");
          statusClass = "badge-light-info";
          break;
        case 20:
          str = t("marketingCampaigns.processing");
          statusClass = "badge-light-warning";
          break;
        case 30:
          str = t("marketingCampaigns.completed");
          statusClass = "badge-light-success";
          break;
        case 40:
          str = t("marketingCampaigns.void");
          statusClass = "badge-light-danger";
          break;
        default:
          break;
      }
      return { str, statusClass };
    };

    const handleFilter = (val) => {
      console.log(val);
      let filterArr: Array<FilterOption> = [];
      for (let item in val) {
        if (val[item] != "") {
          if (item == "start_date") {
            filterArr.push({
              field: item,
              value: val[item],
              condition: "gteq",
            });
          } else if (item == "end_date") {
            filterArr.push({
              field: item,
              value: val[item],
              condition: "lteq",
            });
          } else if (item == "name") {
            filterArr.push({
              field: item,
              value: val[item],
              condition: "like",
            });
          } else {
            filterArr.push({
              field: item,
              value: val[item],
              condition: "eq",
            });
          }
        }
      }
      filterOptions.value.splice(0, filterOptions.value.length, ...filterArr);
      search.value = "";
      currentPage.value = 1;
      getCampaignList(
        currentPage.value,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const handleFilterReset = () => {
      resetFilter();
      getCampaignList(
        currentPage.value,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const resetFilter = () => {
      filterOptions.value.splice(0, filterOptions.value.length);
    };

    const onColumnSort = (val) => {
      let sortObj: SortOption = {
        field: val.columnName,
        direction: val.order,
      };
      sortOptions.value.splice(0, sortOptions.value.length, sortObj);
      getCampaignList(
        currentPage.value,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const onCurrentPageChange = (page) => {
      currentPage.value = page;
      getCampaignList(
        page,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const onRowsPerPageChange = (pagesize) => {
      pageSize.value = pagesize;
      getCampaignList(
        currentPage.value,
        pagesize,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const debounceSearch = _.debounce(getCampaignList, 1000);

    const searchItems = () => {
      currentPage.value = 1;
      debounceSearch(
        currentPage.value,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const removeSearch = () => {
      search.value = "";
      currentPage.value = 1;
      updateList();
    };

    const updateList = () => {
      getCampaignList(
        currentPage.value,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    return {
      t,
      formatDate,
      loading,
      tableHeader,
      tableData,
      currentPage,
      total,
      pageSize,
      search,
      getCampaignList,
      onCurrentPageChange,
      onRowsPerPageChange,
      searchItems,
      removeSearch,
      onColumnSort,
      handleFilter,
      handleFilterReset,
      getStatus,
      updateList,
    };
  },
});
